import type { ReactNode } from 'react'
import { WalletAddressContext, WalletReadyContext } from '@/lib/walletContext'

/**
 * Prerender-only stand-in for `WalletProvider`.
 *
 * The real `WalletProvider` statically imports `wagmi`, which transitively
 * pulls in `@reown/appkit-adapter-wagmi` and WalletConnect internals
 * (`@walletconnect/jsonrpc-ws-connection`, etc). Those packages contain a
 * `require("ws")` fallback for non-browser environments; when Rollup bundles
 * that code into the Node/ESM prerender chunk, Node throws
 * `ReferenceError: require is not defined in ES module scope`, which fails
 * the entire `vite build` (see `vite-prerender-plugin` build step).
 *
 * Since prerendered HTML never has a real wallet session anyway (no
 * `window`, no injected provider), it only needs the same "not ready"
 * defaults the real provider renders before AppKit finishes booting on the
 * client. This component supplies exactly that, without importing `wagmi`,
 * `@reown/appkit-adapter-wagmi`, or `@/components/WalletProvider`, so the
 * WalletConnect module graph is never reached during SSR/prerender.
 *
 * Do not import `wagmi`/AppKit here. Doing so reintroduces the prerender
 * build failure this file exists to avoid.
 */
function WalletProviderStatic({ children }: { children: ReactNode }) {
  return (
    <WalletReadyContext.Provider value={false}>
      <WalletAddressContext.Provider value={undefined}>
        {children}
      </WalletAddressContext.Provider>
    </WalletReadyContext.Provider>
  )
}

export default WalletProviderStatic
